import { useTranslation } from 'react-i18next'
import { useContext, useState, useRef, useEffect, useCallback } from 'react'
import { PaypalButtonsComponent } from '../../../node-admin/payment/paypal-buttons.component'
import {
  useCreatePaypalOrderMutation,
  useGetUserTokensLazyQuery,
} from '../../../../generated/graphql'
import { AuthContext } from '../../../../contexts/auth-context'
import { getPaypalReturnUrls } from '../../../node-admin/payment/paypal.utils'

type PaypalConnectModalProps = {
  visible: boolean
  onDismiss: () => void
  onSuccess: () => void
}

/**
 * Hook to check for PayPal token creation after vault setup
 */
const usePaypalTokenChecker = (
  visible: boolean,
  onSuccess: () => void,
  onDismiss: () => void
) => {
  const [getUserTokens] = useGetUserTokensLazyQuery()
  const [checking, setChecking] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setChecking(false)
  }, [])

  const checkForToken = useCallback(async (): Promise<void> => {
    return new Promise((resolve) => {
      setChecking(true)
      let attempts = 0
      const maxAttempts = 20

      const checkToken = async () => {
        attempts++
        console.log(
          `Checking for PayPal token, attempt ${attempts}/${maxAttempts}`
        )

        try {
          const { data } = await getUserTokens({
            variables: { type: 'PAYMENT' },
            fetchPolicy: 'network-only',
          })
          const hasPaypalToken = data?.userTokens?.some(
            (token) => token.source === 'paypal'
          )
          console.log(
            `hasPaypalToken: ${hasPaypalToken}, attempt: ${attempts}, tokens:`,
            data?.userTokens?.length || 0
          )

          if (hasPaypalToken || attempts >= maxAttempts) {
            cleanup()
            onSuccess()
            onDismiss()
            resolve()
          }
        } catch (error) {
          console.error('Error checking for PayPal token:', error)
          if (attempts >= maxAttempts) {
            cleanup()
            onSuccess()
            onDismiss()
            resolve()
          }
        }
      }

      checkToken()
      intervalRef.current = setInterval(checkToken, 1000)
      timeoutRef.current = setTimeout(() => {
        console.log('Timeout reached, stopping check')
        cleanup()
        onSuccess()
        onDismiss()
        resolve()
      }, 10000)
    })
  }, [getUserTokens, cleanup, onSuccess, onDismiss])

  useEffect(() => {
    if (!visible) {
      cleanup()
    }
    return cleanup
  }, [visible, cleanup])

  return { checking, checkForToken }
}

/**
 * PayPal connection modal for vault token setup
 *
 * Flow:
 * 1. User clicks connect and approves payment in PayPal dialog
 * 2. PayPal captures the minimal order ($0.01)
 * 3. PayPal sends webhook to backend with vault token
 * 4. Backend stores the vault token via webhook handler
 * 5. Frontend refetches user tokens to show connected status
 */
export const PaypalConnectModal = ({
  visible,
  onDismiss,
  onSuccess,
}: PaypalConnectModalProps) => {
  const { t } = useTranslation('payment-methods')
  const [createPaypalOrderMutation] = useCreatePaypalOrderMutation()
  const { userId } = useContext(AuthContext)
  const { checking, checkForToken } = usePaypalTokenChecker(
    visible,
    onSuccess,
    onDismiss
  )

  const createPaypalOrder = async (): Promise<string> => {
    if (!userId) {
      throw new Error('User not found')
    }

    const { returnUrl, cancelUrl } = getPaypalReturnUrls()

    // TODO: Once codegen runs, use createPaypalVaultSetupOrderMutation instead
    // For now, using createPaypalOrder with vault-setup format
    const { data } = await createPaypalOrderMutation({
      variables: {
        input: {
          amount: '0.01',
          currency: 'USD',
          description: 'PayPal payment method setup',
          customId: `vault-setup:${userId}`,
          returnUrl,
          cancelUrl,
        },
      },
    })

    if (!data?.createPaypalOrder?.id) {
      throw new Error('Failed to create PayPal setup order')
    }

    return data.createPaypalOrder.id
  }

  return (
    <PaypalButtonsComponent
      visible={visible}
      onDismiss={onDismiss}
      onCreateOrder={createPaypalOrder}
      onApprove={checkForToken}
      title={
        checking ? t('checking-token') || 'Checking...' : t('connect-paypal')
      }
      successMessage={t('connected-success')}
      errorMessage={t('connect-failed')}
    />
  )
}
